import {
  Scope,
  SolutionMap,
  SolutionOptionKey,
} from "@labs/shared/types/Solution.js";
import { SolutionCopyByScope } from "../types/EmailTemplateData";

const suffixes = {
  [SolutionOptionKey.EnergyEfficiencyAndHeatRecoveryElectricity]:
    "electric intensity reduction",
  [SolutionOptionKey.EnergyEfficiencyAndHeatRecoveryNaturalGas]:
    "natural gas intensity reduction",
  [SolutionOptionKey.EnergyEfficiencyAndHeatRecoveryGasoline]:
    "gasoline intensity reduction",
  [SolutionOptionKey.EnergyEfficiencyAndHeatRecoveryDiesel]:
    "diesel intensity reduction",
  [SolutionOptionKey.EnergyEfficiencyAndHeatRecoveryPropane]:
    "propane intensity reduction",
  [SolutionOptionKey.EnergyEfficiencyAndHeatRecoveryFuelOil]:
    "fuel oil intensity reduction",
  [SolutionOptionKey.StationaryElectrificationNaturalGas]: "of natural gas",
  [SolutionOptionKey.StationaryElectrificationPropane]: "of propane",
  [SolutionOptionKey.StationaryElectrificationFuelOil]: "of fuel oil",
  [SolutionOptionKey.MobileElectrificationGasoline]: "of gasoline",
  [SolutionOptionKey.MobileElectrificationDiesel]: "of diesel",
  [SolutionOptionKey.LowNoCarbonFuelSwitchingNaturalGas]: "of natural gas",
  [SolutionOptionKey.LowNoCarbonFuelSwitchingPropane]: "of propane",
  [SolutionOptionKey.LowNoCarbonFuelSwitchingFuelOil]: "of fuel oil",
  [SolutionOptionKey.LowNoCarbonFuelSwitchingGasoline]: "of gasoline",
  [SolutionOptionKey.LowNoCarbonFuelSwitchingDiesel]: "of diesel",
  [SolutionOptionKey.RenewablePurchaseFromUtilityElectricity]:
    "emissions-free power purchase from utility",
  [SolutionOptionKey.RenewableEnergyAsPpaVppaElectricity]:
    "emissions-free power from PPAs/VPPAs",
  [SolutionOptionKey.RenewableCreditsRecsElectricity]:
    "of purchased electricity is offset using RECs",
  [SolutionOptionKey.CoGenerationElectricity]: "from onsite co-generation",
  [SolutionOptionKey.OnsiteSolarOrRenewableEnergyGenerationElectricity]:
    "from onsite renewables",
  [SolutionOptionKey.CarbonCaptureAndSequestrationCarbon]:
    "footprint reduction using carbon capture",
  [SolutionOptionKey.EliminateFugitiveEmissionsCarbon]:
    "footprint reduction by eliminating fugitive emissions",
  [SolutionOptionKey.OffsetsCarbon]: "footprint reduction through offsets",
  [SolutionOptionKey.RefrigerantLeaksRefrigerants]:
    "footprint reduction of refrigerant leaks",
  [SolutionOptionKey.LowGwpRefrigerantsRefrigerants]:
    "average reduction in refrigerant GWP",
};

const filterSolutions = (keys: SolutionOptionKey[], solutionMap: SolutionMap) =>
  keys
    .filter((key) => (solutionMap[key] || 0) > 0)
    .map((key) => `${solutionMap[key]}% ${suffixes[key]}`);

export const generateSolutionCopyByScope = (
  solutionMap: SolutionMap,
): SolutionCopyByScope => {
  const scope1Solutions = {
    // todo: extract this mapping into a shared location for reuse with solutionsNavigationData
    cutEnergyWaste: filterSolutions(
      [
        SolutionOptionKey.EnergyEfficiencyAndHeatRecoveryNaturalGas,
        SolutionOptionKey.EnergyEfficiencyAndHeatRecoveryPropane,
        SolutionOptionKey.EnergyEfficiencyAndHeatRecoveryDiesel,
        SolutionOptionKey.EnergyEfficiencyAndHeatRecoveryFuelOil,
        SolutionOptionKey.EnergyEfficiencyAndHeatRecoveryGasoline,
      ],
      solutionMap,
    ),
    electrify: filterSolutions(
      [
        SolutionOptionKey.StationaryElectrificationNaturalGas,
        SolutionOptionKey.StationaryElectrificationPropane,
        SolutionOptionKey.MobileElectrificationDiesel,
        SolutionOptionKey.StationaryElectrificationFuelOil,
        SolutionOptionKey.MobileElectrificationGasoline,
      ],
      solutionMap,
    ),
    buyClean: filterSolutions(
      [
        SolutionOptionKey.LowNoCarbonFuelSwitchingNaturalGas,
        SolutionOptionKey.LowNoCarbonFuelSwitchingPropane,
        SolutionOptionKey.LowNoCarbonFuelSwitchingDiesel,
        SolutionOptionKey.LowNoCarbonFuelSwitchingFuelOil,
        SolutionOptionKey.LowNoCarbonFuelSwitchingGasoline,
      ],
      solutionMap,
    ),
    lessCommonSolutions: filterSolutions(
      [
        SolutionOptionKey.CarbonCaptureAndSequestrationCarbon,
        SolutionOptionKey.EliminateFugitiveEmissionsCarbon,
        SolutionOptionKey.OffsetsCarbon,
        SolutionOptionKey.RefrigerantLeaksRefrigerants,
        SolutionOptionKey.LowGwpRefrigerantsRefrigerants,
      ],
      solutionMap,
    ),
  };

  const scope2Solutions = {
    cutEnergyWaste: filterSolutions(
      [SolutionOptionKey.EnergyEfficiencyAndHeatRecoveryElectricity],
      solutionMap,
    ),
    buyClean: filterSolutions(
      [
        SolutionOptionKey.RenewablePurchaseFromUtilityElectricity,
        SolutionOptionKey.RenewableEnergyAsPpaVppaElectricity,
        SolutionOptionKey.RenewableCreditsRecsElectricity,
        SolutionOptionKey.CoGenerationElectricity,
        SolutionOptionKey.OnsiteSolarOrRenewableEnergyGenerationElectricity,
      ],
      solutionMap,
    ),
  };

  return {
    [Scope.Scope1]: scope1Solutions,
    [Scope.Scope2]: scope2Solutions,
  };
};
